import waitForElement from "./waitForElement"

const PRIMER_LOCALES = [
  "de",
  "el",
  "en",
  "es",
  "fr",
  "pl",
  "pt",
  "bg",
  "hu",
  "ro",
  "ru",
  "sk",
  "nl",
]

const PRIMER_CONFIG = {
  container: "#primer-checkout-container",
  locale: pipelineConfig.language || "en",
  vault: {
    visible: false,
  },
  successScreen: false,
  errorMessage: {
    disabled: true,
  },
  card: {
    cardholderName: {
      placeholder: primerTranslations.name_placeholder,
    },
    cardNumber: {
      placeholder: "1234 1234 1234 1234",
    },
    expiryDate: {
      placeholder: primerTranslations.expiry_placeholder,
    },
    cvv: {
      placeholder: "●●●",
    },
  },
  paypal: {
    buttonColor: "gold",
    buttonShape: "rect",
    paymentFlow: "PREFER_VAULT",
  },
  googlePay: {
    captureBillingAddress: true,
  },
  applePay: {
    captureBillingAddress: true,
  },
  style: {
    fontFaces: [
      {
        fontFamily: "Acumin Pro",
        src: `url("${window.origin}${pipelineConfig.pathPrefix}fonts/AcuminPro-Book.woff2") format("woff2")`,
      },
    ],
    input: {
      base: {
        fontFamily: "'Acumin Pro', Arial, sans-serif",
        fontSize: "16px",
        lineHeight: "1.25",
        color: "#212121",
        background: "#fff",
        borderColor: "#f0f0f0",
        borderRadius: "3px",
        paddingHorizontal: 12,
        height: 36 + 2, // primer uses -2
        placeholder: {
          color: "#868c8e",
        },
      },
    },
  },
  form: {
    inputLabelsVisible: false,
  },
}

export default () => ({
  primerLoading: true,

  submitting: false,

  paymentMethod: null,

  error: null,
  initError: null,

  // Checks for the compability with locale
  isPrimerLocale(locale) {
    return PRIMER_LOCALES.includes(locale || "en")
  },

  addConfirmationEventListener() {
    function observeElementInsertion(selector) {
      if (!window.MutationObserver) {
        console.error("MutationObserver is not supported in this browser.")
        return
      }

      // Callback function for when a mutation occurs
      const observerCallback = (mutationsList, observer) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            // Check if the element now exists in the DOM
            const element = document.querySelector(selector)

            if (element) {
              observer.disconnect()
              break
            }
          }
        }
      }

      // Create a MutationObserver with the callback function
      const observer = new MutationObserver(observerCallback)

      // Start observing the document's body for added nodes
      observer.observe(document.body, { childList: true, subtree: true })
    }

    try {
      const primerCheckoutCardNumberId = "#primer-checkout-card-number-field"
      observeElementInsertion(primerCheckoutCardNumberId)
    } catch (error) {
      console.error("Error in addConfirmationEventListener", error)
    }
  },

  async initPrimer({ checkoutCompletePath, failedCheckoutPath, external }) {
    const primer = document.getElementById("primer")

    if (document.getElementById("primer-checkout-submit-button-container")) {
      return window.location.reload()
    }

    const searchParams = new URLSearchParams(window.location.search)
    if (this.store.values.hideApm) {
      primer.classList.add("apm-buttons-hidden")
    }
    const primerErrorSearchParam = searchParams.get("primer_error")
    if (primerErrorSearchParam) {
      this.error =
        primerTranslations.errors[primerErrorSearchParam] || primerTranslations.errors.default
      requestAnimationFrame(() => this.scrollToPrimerOrPaymodes())
    }

    this.primerLoading = true

    await waitForElement("#primer-submit-button")

    const submitButton = document.getElementById("primer-submit-button")
    const orBlock = document.getElementById("primer-or-block")

    let clientSession, checkout

    try {
      clientSession = this.store.values.primerSession || (await this.store.payOrCreateSession(true))
      checkout = await this.showUniversalCheckout({
        checkoutCompletePath,
        failedCheckoutPath,
        clientSession,
        submitButton,
        external,
      })
    } catch (error) {
      try {
        clientSession = await this.store.payOrCreateSession(true)
        checkout = await this.showUniversalCheckout({
          checkoutCompletePath,
          failedCheckoutPath,
          clientSession,
          submitButton,
          external,
        })
      } catch (error) {
        this.initError = true
      }
    }

    this.primerLoading = false

    if (external)
      document.getElementById("primer-checkout-other-payment-methods").after(submitButton)

    submitButton.classList.remove("hidden")
    submitButton.addEventListener(
      "click",
      () => {
        checkout.submit()
      },
      false
    )

    const primerCheckoutCardForm = document.getElementById("primer-checkout-card-form")
    if (primerCheckoutCardForm && orBlock) {
      primerCheckoutCardForm.before(orBlock)
      orBlock.classList.remove("hidden")
    }

    this.patchTranslations(checkout)
  },

  async showUniversalCheckout({
    checkoutCompletePath,
    failedCheckoutPath,
    clientSession,
    submitButton,
    external,
  }) {
    if (!clientSession) {
      this.initError = true
      return
    }

    if (clientSession.instantlyCharged) {
      window.location = this.store.addTokenHashParam(checkoutCompletePath)
      return
    }

    const { clientToken } = clientSession
    let selectedPaymentMethodType = null

    const checkout = await Primer.showUniversalCheckout(clientToken, {
      ...PRIMER_CONFIG,
      submitButton: {
        useBuiltInButton: false,
        onVisible: () => {
          submitButton.classList.remove("hidden")
        },
        onDisable: (isDisabled) => {
          submitButton.disabled = isDisabled
          this.listenExitIntent = !isDisabled
        },
        onLoading: () => {
          submitButton.classList.remove("hidden")
          submitButton.classList.add("loading")
        },
      },

      onBeforePaymentCreate: ({ paymentMethodType }, handler) => {
        this.paymentMethod = paymentMethodType
        return handler.continuePaymentCreation()
      },

      onPaymentCreationStart: () => {
        this.store.trackViewAndMaybeStatus("SUBMIT_CC_INPUT")
      },

      onCheckoutComplete: ({ payment }) => {
        this.submitting = false

        let url = checkoutCompletePath
        if (clientSession) {
          const clientSessionWithPrimer = {
            ...clientSession,
            providerId: payment.id,
          }
          url = this.store.addTokenHashParam(
            `${checkoutCompletePath}?${this.store
              .buildUrlParamsForThankYou(clientSessionWithPrimer)
              .toString()}`
          )
        }

        this.store.clearPrimerSession()
        window.location = url
      },

      onCheckoutFail: (error, { payment }, handler) => {
        this.submitting = false
        this.listenExitIntent = true

        if (!handler) {
          return !external
            ? this.reloadWithError()
            : this.redirectToFailedCheckoutPath(failedCheckoutPath)
        }

        if (!external) {
          this.store.trackViewAndMaybeStatus("FAILED_CHECKOUT")
          handler.showErrorMessage()
          return this.reloadWithError()
        } else {
          this.redirectToFailedCheckoutPath(failedCheckoutPath)
        }
      },

      onPaymentMethodAction: (action, data) => {
        if (
          action === "PAYMENT_METHOD_SELECTED" &&
          data.paymentMethodType !== selectedPaymentMethodType
        ) {
          selectedPaymentMethodType = data.paymentMethodType
        }
      },
    })

    this.store.trackViewAndMaybeStatus("INITIATE_CHECKOUT")

    return checkout
  },

  reloadWithError() {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set("primer_error", this.paymentMethod)
    window.history.replaceState(
      {},
      "",
      this.store.addTokenHashParam(
        `${window.location.origin}${window.location.pathname}?${searchParams}`
      )
    )
    return window.location.reload()
  },

  redirectToFailedCheckoutPath(failedCheckoutPath) {
    this.store.values.paymentMethod = this.paymentMethod
    window.location = this.store.addTokenHashParam(failedCheckoutPath)
  },

  applyCustomLabel(id, text) {
    const currentField = window.document.getElementById(id)

    if (!currentField) return

    // Create our label
    const newLabel = window.document.createElement("div")
    newLabel.dataset.testid = "CreditCardForm.inputLabel"
    newLabel.classList.add("PrimerCheckout__label")
    newLabel.innerHTML = text

    // Display our label
    currentField.prepend(newLabel)
  },

  applyCustomErrorMessage(id, text) {
    const currentField = document.getElementById(id)
    if (!currentField) return

    const targetNode = currentField.getElementsByTagName("div")[0]
    if (!targetNode) return

    // Mutation config
    const config = { childList: true }

    // Callback function executes on dom changes
    const callback = (mutationList) => {
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          // Apply our custom translation
          if (targetNode.innerHTML !== text && targetNode.innerHTML !== "") {
            targetNode.innerHTML = text
          }
        }
      }
    }
    // Create/Observe the node mutations
    const observer = new MutationObserver(callback)
    observer.observe(targetNode, config)
  },

  // Patch the primer translations with ours
  patchTranslations(checkout) {
    this.applyCustomLabel("primer-checkout-card-number-field", primerTranslations.card_number)
    this.applyCustomLabel("primer-checkout-card-expirydate-field", primerTranslations.expiry)
    this.applyCustomLabel("primer-checkout-card-cvv-field", primerTranslations.cvv)
    this.applyCustomLabel("primer-checkout-cardholder-name-field", primerTranslations.name)

    if (!this.isPrimerLocale(pipelineConfig.language)) {
      this.applyCustomErrorMessage(
        "primer-checkout-card-number",
        primerTranslations.errors.empty_field
      )
      this.applyCustomErrorMessage(
        "primer-checkout-card-expirydate-error",
        primerTranslations.errors.empty_field
      )
      this.applyCustomErrorMessage(
        "primer-checkout-card-cvv-error",
        primerTranslations.errors.empty_field
      )
      this.applyCustomErrorMessage(
        "primer-checkout-cardholder-name-error",
        primerTranslations.errors.empty_field
      )
    }
  },

  setError() {
    this.error = primerTranslations.errors[this.paymentMethod]
  },

  scrollToPrimerOrPaymodes() {
    const primer = document.getElementById("primer")
    const paymodes = document.getElementById("paymodes")

    if (primer) {
      primer.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" })
    } else if (paymodes)
      paymodes.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" })
  },
})
